import React, { useEffect } from "react";
import { Form, Input, Select, Table, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { formatMoney } from "../../../../helpers/formatter";
import { getCurrencyInfo } from "../../helper";
import { updateFormData } from "../../../../redux/actions/investmentForm";
import { useInvestmentFunderSelection } from "../../../../custom-hooks/useInvestmentFunderSelection";
import styles from "../Components.module.scss";
import "../select-overrides.css";

const { Option } = Select;

// Simplified props interface
interface InvestmentModalFunderSelectionProps {
  form: any;
  data?: any;
  allInvestmentProducts?: any[];
  handleCurrency?: (currency: string) => void;
}

const InvestmentModalFunderSelection: React.FC<
  InvestmentModalFunderSelectionProps
> = ({ form, data, allInvestmentProducts, handleCurrency }) => {
  const dispatch = useDispatch();

  // Use our custom hook
  const {
    investorOptions,
    wallets,
    investmentProducts,
    searchLoading,
    selectedInvestorInfo,
    relationshipManagers,
    relationshipManagersLoading,
    fetchInvestors,
    handleInvestorChange,
    handleCurrencyChange,
    handleRelationshipManagerChange,
    walletLoading,
  } = useInvestmentFunderSelection(
    form,
    data,
    allInvestmentProducts,
    handleCurrency,
  );

  // Table columns for wallet display
  const columns = [
    {
      title: "Legal Entity",
      dataIndex: "legalEntity",
      key: "legalEntity",
    },
    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
    },
    {
      title: "Account Name",
      dataIndex: "accountName",
      key: "accountName",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text: number, record: any) => formatMoney(text, record.currency),
    },
  ];

  // Make Next button state available to parent
  useEffect(() => {
    form.setFieldValue("walletLoading", walletLoading);
  }, [walletLoading, form]);

  return (
    <div
      className={`investment-modal-funder-selection ${styles.formContainer}`}
    >
      <div className={styles.modalTitle}>Create Investment</div>

      <div className={styles.formWrapper}>
        {/* Hidden field to store raw funder_id value */}
        <Form.Item name="raw_funder_id" hidden={true}>
          <Input type="hidden" />
        </Form.Item>

        {/* Hidden field to track wallet loading state */}
        <Form.Item name="walletLoading" hidden={true}>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          name="funder_id"
          label={<span className={styles.formLabel}>Select Investor</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Please select an investor" }]}
        >
          <Select
            showSearch
            placeholder="search investors"
            filterOption={false}
            onSearch={fetchInvestors}
            onChange={handleInvestorChange}
            dropdownStyle={{ zIndex: 1100 }}
            className={styles.selectStyle}
            style={{ width: "100%" }}
            size="large"
            listHeight={320}
            labelInValue={true}
            options={investorOptions}
            optionRender={(option) => {
              const { data } = option;
              const customerType = data.customerType as string;

              return (
                <div className={styles.investorOption}>
                  <div className={styles.investorOptionHeader}>
                    <span className={styles.investorOptionName}>
                      {(data.label as string)?.split(" /")[0]}
                    </span>
                    <span className={styles.investorOptionBadge}>
                      {customerType || "N/A"}
                    </span>
                  </div>
                  <div className={styles.investorOptionDetails}>
                    {data.email && (
                      <span className={styles.investorOptionDetail}>
                        <span className={styles.investorOptionDetailLabel}>
                          Email:
                        </span>
                        {data.email as string}
                      </span>
                    )}
                    {data.phoneNumber && (
                      <span className={styles.investorOptionDetail}>
                        <span className={styles.investorOptionDetailLabel}>
                          Phone:
                        </span>
                        {data.phoneNumber as string}
                      </span>
                    )}
                    {customerType?.toLowerCase() === "individual" && (
                      <span className={styles.investorOptionDetail}>
                        <span className={styles.investorOptionDetailLabel}>
                          BVN:
                        </span>
                        {(data.bvn as string) || "N/A"}
                      </span>
                    )}
                    {customerType?.toLowerCase() === "corporate" && (
                      <span className={styles.investorOptionDetail}>
                        <span className={styles.investorOptionDetailLabel}>
                          RC No:
                        </span>
                        {(data.rcNumber as string) || "N/A"}
                      </span>
                    )}
                    <span className={styles.investorOptionDetail}>
                      <span className={styles.investorOptionDetailLabel}>
                        Customer ID:
                      </span>
                      {data.value as number}
                    </span>
                  </div>
                </div>
              );
            }}
            notFoundContent={
              searchLoading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              ) : null
            }
            loading={searchLoading}
            defaultValue={
              selectedInvestorInfo
                ? {
                    value: selectedInvestorInfo.value,
                    label: selectedInvestorInfo.label,
                  }
                : undefined
            }
          />
        </Form.Item>

        {/* Relationship Manager dropdown */}
        <Form.Item
          name="relationship_manager_id"
          label={<span className={styles.formLabel}>Relationship Manager</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Select
            placeholder="Select relationship manager"
            className={styles.selectStyle}
            style={{ width: "100%" }}
            size="large"
            onChange={handleRelationshipManagerChange}
            loading={relationshipManagersLoading}
            notFoundContent={
              relationshipManagersLoading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              ) : null
            }
          >
            {relationshipManagers.map((manager) => (
              <Option key={manager.id} value={manager.id}>
                {`${manager.firstName} ${manager.lastName}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="investment_product_id"
          label={
            <span className={styles.formLabel}>
              Select Investment Product Detail
            </span>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            { required: true, message: "Please select investment product" },
          ]}
        >
          <Select
            placeholder="select investment product detail"
            className={styles.selectStyle}
            style={{ width: "100%" }}
            size="large"
            labelInValue
            onChange={(selected) => {
              // With labelInValue, the value comes as an object: { value, label }
              const value = selected.value;
              // Check if value is a combined ID-currency string
              if (typeof value === "string" && value.includes("-")) {
                const [productId, currencyCode] = value.split("-");

                // Find the selected detail from the product details
                const selectedDetail = data?.investment_product_details?.find(
                  (detail: any) => detail.currency_code === currencyCode,
                );

                // Get the product detail ID
                let productDetailId = selectedDetail?.id;

                // If we couldn't find it directly, try to locate it in all product details
                if (!productDetailId && investmentProducts.length > 0) {
                  const product = investmentProducts.find(
                    (p) => p.id.toString() === productId,
                  );
                  if (product?.investment_product_details) {
                    const detail = product.investment_product_details.find(
                      (d: any) => d.currency_code === currencyCode,
                    );
                    productDetailId = detail?.id;
                  }
                }

                // Find the product name to display
                const productName =
                  data?.name ||
                  investmentProducts.find((p) => p.id.toString() === productId)
                    ?.name ||
                  "";

                // Store combined display value for the investment_product_id field
                const displayValue = `${productName} ${currencyCode}`;

                // Update form with product ID, currency, and legal entity
                form.setFieldsValue({
                  // The form field needs to store the display value since we're using labelInValue
                  investment_product_id: {
                    key: productId,
                    value: `${productId}-${currencyCode}`,
                    label: displayValue,
                  },
                  currency: currencyCode,
                  mifos_tenant: selectedDetail?.legal_entity || "",
                });

                // Update Redux state - store the actual ID for processing
                dispatch(
                  updateFormData({
                    investment_product_id: productId,
                    currency: currencyCode,
                    mifos_tenant: selectedDetail?.legal_entity || "",
                    investment_product_detail_id: productDetailId,
                  }),
                );

                // Call currency change handler if available - this will update the currency in other places
                if (handleCurrency) {
                  handleCurrency(currencyCode);
                }
              } else {
                // Just update the product ID for regular option selection
                dispatch(updateFormData({ investment_product_id: value }));
                // Since this is a fallback case, we're not expecting it with the new pattern
                console.warn(
                  "Unexpected format for investment product selection:",
                  value,
                );
              }
            }}
          >
            {data && data.investment_product_details
              ? // If we have a specific product with details, create options for each currency
                data.investment_product_details.map(
                  (detail: any, index: number) => (
                    <Option
                      key={`${data.id}-${detail.currency_code}`}
                      value={`${data.id}-${detail.currency_code}`}
                    >
                      {`${data.name} ${detail.currency_code}`}
                    </Option>
                  ),
                )
              : // Otherwise show all products from the list with their respective currencies
                investmentProducts.flatMap(
                  (product: any) =>
                    product.investment_product_details?.map((detail: any) => (
                      <Option
                        key={`${product.id}-${detail.currency_code}`}
                        value={`${product.id}-${detail.currency_code}`}
                      >
                        {`${product.name} ${detail.currency_code}`}
                      </Option>
                    )) || [],
                )}
          </Select>
        </Form.Item>

        <Form.Item
          name="mifos_tenant"
          label={
            <span className={styles.formLabel}>Mifos Tenant/ Legal Entity</span>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          // Same validation structure as currency field
          rules={[{ required: true, message: "Mifos tenant is required" }]}
        >
          <Input
            placeholder="mifos tenant"
            disabled
            className={`${styles.inputStyle} ${styles.disabledInput}`}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="currency"
          label={<span className={styles.formLabel}>Currency</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Currency is required" }]}
        >
          <Input
            placeholder="Currency"
            disabled
            className={`${styles.inputStyle} ${styles.disabledInput}`}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          label={<span className={styles.formLabel}>Wallet</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {walletLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
              <div style={{ marginTop: "10px" }}>Loading wallet details...</div>
            </div>
          ) : wallets.length > 0 ? (
            <Table
              dataSource={wallets}
              columns={columns}
              pagination={false}
              size="middle"
              style={{ marginBottom: "20px" }}
            />
          ) : (
            <div
              style={{ padding: "10px", textAlign: "center", color: "#999" }}
            >
              No wallet details available. Select an investor and an investment
              product.
            </div>
          )}
        </Form.Item>

        <Form.Item
          name="effective_date"
          label="Effective Date"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Please select effective date" }]}
          style={{ display: "none" }}
        >
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          name="transaction_receipt"
          label="Upload Transaction Receipt"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            {
              required: true,
              message: "Please upload the transaction receipt",
            },
          ]}
          style={{ display: "none" }}
        >
          <Input type="hidden" />
        </Form.Item>
      </div>
    </div>
  );
};

export default InvestmentModalFunderSelection;
